import { useCallback, useEffect, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import isNull from "lodash-es/isNull"
import { FormProvider, useForm } from "react-hook-form"
import { type Location, useLocation, useParams } from "react-router-dom"

import useQueryParam from "$/hooks/use-query-param"
import { getErrorMessages } from "$/utils/get-error-messages"

import { Button } from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { ServerStateKeys } from "@/constants"
import { useRespondentSurvey, useRespondentSurveySubmit } from "@/entities/survey"
import { SurveySubmitData } from "@/entities/survey/models"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { EStorageServiceType, LocalStorageServiceKeys, StorageService } from "@/services"
import CareTeamLink from "@/shared/CareTeamLink"
import { WarningMessage } from "@/shared/WarningMessage"
import ErrorTooltip from "@/shared/error-page/ErrorTooltip"
import { FullSpinner } from "@/shared/spinner"
import OwlErrorLogo from "@/svgs/OwlErrorLogo"

import { createValidationSchema } from "../validation"

import { FeedbackSurvey } from "./FeedbackSurvey"
import { Survey } from "./Survey"

import "./styles.scss"

const storageService: StorageService = new StorageService(EStorageServiceType.Local)

export const StakeHolderSurvey = () => {
    const { refetch: refetchUser }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const { t }: IUseTranslation = useTranslation()
    const query = useQueryParam()
    const params = useParams()
    const accessCode = params.access_code || query.get("access_code")
    const [error, setError] = useState(null)
    const {
        data,
        isLoading,
        isSuccess,
        isError,
        isFetching,
        error: surveyError,
        refetch
    } = useRespondentSurvey(accessCode)
    const submitSurvey = useRespondentSurveySubmit(accessCode)

    const [surveySubmitLoading, setSurveySubmitLoading] = useState(false)

    const location: Location = useLocation()

    const queryClient: QueryClient = useQueryClient()

    const methods = useForm({
        shouldFocusError: true,
        resolver: data?.questions && isSuccess ? yupResolver(createValidationSchema(data.questions)) : undefined
    })

    const { handleSubmit, reset } = methods

    useEffect(() => {
        if (data && isSuccess && !data.message) {
            reset(
                data.questions.reduce((acc, question) => {
                    return {
                        [question.id]: {
                            free_response: null,
                            answer: null
                        }
                    }
                }, {})
            )
        }
    }, [data, isSuccess, reset])

    const submitFeedback = useCallback(
        values => {
            const submitData: SurveySubmitData[] = data.questions.map(question => {
                const value = values[question.id]
                const trimmedResponse = value?.free_response?.trim()

                const freeResponse = !!trimmedResponse ? trimmedResponse : null
                return {
                    question: question.id,
                    answer: value?.answer ? +value.answer : null,
                    free_response: freeResponse
                }
            })

            setSurveySubmitLoading(true)

            if (isNull(accessCode)) throw new Error("Access code can't be null")

            submitSurvey.mutate(
                { response: submitData },
                {
                    onSuccess: () => {
                        refetch().finally(() => {
                            setSurveySubmitLoading(false)
                        })

                        if (location?.state?.shouldBackButtonRedirectToSurveysPage) {
                            queryClient.refetchQueries({ queryKey: [ServerStateKeys.Surveys] })

                            refetchUser()?.then(response => {
                                if (
                                    !response?.data?.areNotCompletedSurveysPresent &&
                                    storageService.getItem(
                                        LocalStorageServiceKeys.SurveysPageRedirected(response?.data?.id)
                                    )
                                ) {
                                    storageService.removeItem(
                                        LocalStorageServiceKeys.SurveysPageRedirected(response.data.id)
                                    )
                                }
                            })
                        }
                    },
                    onError: (error: AxiosError): void => setError(getErrorMessages(error))
                }
            )
        },
        [queryClient, submitSurvey, refetch, refetchUser, location, accessCode]
    )

    if ((isLoading || isFetching || surveySubmitLoading) && !isSuccess) {
        return <FullSpinner />
    }

    if (isError && surveyError) {
        return (
            <div className="d-flex flex-column align-items-center">
                <ErrorTooltip>
                    <p className="text-center m-0">{getErrorMessages(surveyError).join("")}</p>
                </ErrorTooltip>
                <div className="mt-5 d-flex justify-content-center">
                    <OwlErrorLogo />
                </div>
                <CareTeamLink variant="brand" text="Need help?" className="care-link" />
            </div>
        )
    }

    return (
        <div className="stakeholder-survey">
            <FormProvider {...methods}>
                {data.message ? (
                    <FeedbackSurvey status={data.message} isSelfAssessment={data?.isSelfAssessment} />
                ) : (
                    <Survey survey={data} />
                )}
            </FormProvider>
            {!data.message && (
                <>
                    <WarningMessage message={error} />
                    <div className="mx-auto w-max">
                        <Button
                            isBusy={isFetching || submitSurvey.isPending || methods.formState.isSubmitting}
                            onClick={handleSubmit(submitFeedback)}
                        >
                            {t("common.submit")}
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}
